import { Injectable } from '@angular/core';
import { Geolocation, GeolocationOptions, Geoposition } from '@awesome-cordova-plugins/geolocation/ngx';
import { Guid } from '@models/guid';
import { LocationConfig } from '@techwan/ionic-plugin-location';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { ILocationProvider } from '../../../schema/interfaces/location-provider';
import { LocationConfigService } from '../../location-config/location-config.service';
import { PositionProxyService } from '../position-proxy/position-proxy.service';

@Injectable()
export class ForegroundLocationService implements ILocationProvider {
  private readonly positionOptions: GeolocationOptions = { maximumAge: 60000, timeout: 120000, enableHighAccuracy: true };

  private readonly _current = new BehaviorSubject<Geoposition>(null);
  get $change(): Observable<Geoposition> {
    return this._current.asObservable();
  }

  private watchPositionSub: Subscription = null;

  private config: LocationConfig;
  private locationConfigChangeSub: Subscription;

  constructor(
    private geolocation: Geolocation,
    private positionProxy: PositionProxyService,
    private locationConfig: LocationConfigService
  ) {}

  getCurrent(): Promise<Geoposition> {
    return this.geolocation.getCurrentPosition({ timeout: 40000, enableHighAccuracy: true });
  }

  setup(uid: Guid) {
    this.positionProxy.init(uid);
  }

  start(): Observable<void> {
    return this.locationConfig.init().pipe(
      first(),
      map(locationConfig => {
        this.locationConfigChangeSub = this.locationConfig.$change.subscribe(locationConfig => this.configure(locationConfig));
        this.configure(locationConfig);

        if (this.watchPositionSub === null) {
          this.watchPositionSub = this.geolocation
            .watchPosition(this.positionOptions)
            .pipe(filter((position: Geoposition) => position?.coords !== undefined))
            .subscribe((position: Geoposition) => {
              console.debug('New position: ', position);
              const oldPosition: Geoposition = this._current.value;
              this._current.next(position);

              if (this.shouldSend(oldPosition, position)) {
                this.positionProxy.updatePosition(position);
              }
            });
        }
      })
    );
  }

  private configure(locationConfig: LocationConfig) {
    this.config = locationConfig;

    if (this.config.LocationTrackingEnabled && !this.positionProxy.isRunning) {
      this.positionProxy.start();
    } else if (!this.config.LocationTrackingEnabled && this.positionProxy.isRunning) {
      this.positionProxy.stop();
    }
  }

  private shouldSend(oldPosition: Geoposition, newPosition: Geoposition): boolean {
    if (this.config.LocationTrackingEnabled) {
      if (newPosition) {
        return true;
      }
    }

    return false;
  }

  stop(): Promise<void> {
    if (this.watchPositionSub !== null) {
      this.watchPositionSub.unsubscribe();
      this.watchPositionSub = null;
    }

    this.locationConfigChangeSub?.unsubscribe();
    this.locationConfig.uninit();
    this.positionProxy.stop();

    return Promise.resolve();
  }
}
